import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Collapse, 
  IconButton, 
  Typography,
  TextField,
  TablePagination,
  TableSortLabel,
  Paper,
  InputAdornment,
  Chip
} from '@mui/material';
import { 
  KeyboardArrowDown, 
  KeyboardArrowUp, 
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useStyles } from './styles';
import { useSelector } from 'react-redux';

const SubscriberRow = ({ subscriber, classes }) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <>
      <TableRow className={classes.tableRow}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={handleToggle}
            className={classes.expandButton}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell className={classes.emailCell}>
          <Typography className={classes.emailText}>
            {subscriber.email}
          </Typography>
        </TableCell>
        <TableCell className={classes.countCell}>
          <Typography className={classes.countText}>
            {subscriber.totalSubscriptions}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className={classes.collapseBox}>
              <Typography variant="h6" className={classes.subscriptionsTitle}>
                Subscriptions
              </Typography>
              <Table size="small" className={classes.subscriptionsTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.subscriptionHeader}>Podcast Name</TableCell>
                    <TableCell className={classes.subscriptionHeader}>Artist</TableCell>
                    <TableCell className={classes.subscriptionHeader}>Genre</TableCell>
                    <TableCell className={classes.subscriptionHeader}>Rank</TableCell>
                    <TableCell className={classes.subscriptionHeader}>Country</TableCell>
                    <TableCell className={classes.subscriptionHeader}>Day of Week</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriber.subscriptions.map((subscription, index) => (
                    <TableRow key={subscription.notifyId || index}>
                      <TableCell className={classes.subscriptionCell}>
                        {subscription.podcast.name}
                      </TableCell>
                      <TableCell className={classes.subscriptionCell}>
                        {subscription.podcast.artist}
                      </TableCell>
                      <TableCell className={classes.subscriptionCell}>
                        {subscription.podcast.genre}
                      </TableCell>
                      <TableCell className={classes.subscriptionCell}>
                        {subscription.podcast.rank}
                      </TableCell>
                      <TableCell className={classes.subscriptionCell}>
                        {subscription.country || 'N/A'}
                      </TableCell>
                      <TableCell className={classes.subscriptionCell}>
                        {subscription.dayOfWeek || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const Subscribers = ({ 
  subscribers = [], 
  loading = false, 
  onSearch,
  onSort,
  onPageChange,
  totalCount = 0,
  page = 0,
  rowsPerPage = 25,
  sortBy = '',
  sortOrder = 'asc'
}) => {
  const { isLight } = useSelector((state) => state.theme);
  const classes = useStyles({ theme: isLight ? 'light' : 'dark' });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback((query) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (onSearch) {
        onSearch({
          search: query
        });
      }
    }, 500); // 500ms delay
    
    setSearchTimeout(timeout);
  }, [onSearch, searchTimeout]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    debouncedSearch('');
  };

  const handleSort = (column) => {
    if (onSort) {
      const newOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
      onSort(column, newOrder);
    }
  };

  const handleChangePage = (event, newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (onPageChange) {
      onPageChange(0, newRowsPerPage);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <Box className={classes.container}>
      <Typography variant="h4" className={classes.title}>
        Subscribers Management
      </Typography>
      
             {/* Search and Filters Section */}
       <Paper className={classes.filtersContainer}>
         <Box className={classes.searchContainer}>
           <TextField
             label="Search by Email or Podcast Name"
             variant="outlined"
             size="small"
             value={searchQuery}
             onChange={handleSearchChange}
             className={classes.searchField}
             placeholder="Enter email address or podcast name..."
             InputProps={{
               startAdornment: (
                 <InputAdornment position="start">
                   <SearchIcon className={classes.searchIcon} />
                 </InputAdornment>
               ),
               endAdornment: searchQuery && (
                 <InputAdornment position="end">
                   <IconButton size="small" onClick={handleClearSearch}>
                     <ClearIcon className={classes.clearIcon} />
                   </IconButton>
                 </InputAdornment>
               ),
             }}
           />
         </Box>

         {/* Active Filters */}
         {searchQuery && (
           <Box className={classes.activeFilters}>
             <Typography variant="body2" className={classes.activeFiltersLabel}>
               Active filters:
             </Typography>
             <Chip
               label={`Search: ${searchQuery}`}
               onDelete={handleClearSearch}
               size="small"
               className={classes.filterChip}
             />
           </Box>
         )}
      </Paper>

      {/* Table Section */}
      <Box className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell className={classes.headerCell}>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                  className={classes.sortLabel}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.headerCell}>
                <TableSortLabel
                  active={sortBy === 'totalSubscriptions'}
                  direction={sortBy === 'totalSubscriptions' ? sortOrder : 'asc'}
                  onClick={() => handleSort('totalSubscriptions')}
                  className={classes.sortLabel}
                >
                  Total Subscriptions
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && subscribers.length > 0 ? (
              subscribers.map((subscriber, index) => (
                <SubscriberRow 
                  key={subscriber.email || index} 
                  subscriber={subscriber} 
                  classes={classes} 
                />
              ))
            ) : !loading ? (
              <TableRow>
                <TableCell colSpan={3} className={classes.noDataCell}>
                  <Typography className={classes.noDataText}>
                    {searchQuery ? 'No subscribers found matching your search' : 'No subscribers found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          className={classes.pagination}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Box>
    </Box>
  );
}; 